import React, { PropsWithChildren, useEffect, useRef } from 'react';
import { MemoryRouter, useNavigate } from 'react-router-dom';
import { useMedia } from 'react-use';
import { useResizeScale } from './modules/scene/hooks/useResizeScale';
import { useGameEvents } from './modules/game/hooks/useGameEvents';
import usePreventZoom from './hooks/usePreventZoom';
import { maxPhoneWidth, maxTabletWidth, maxLandscapePhoneHeight, maxLandscapePhoneWidth } from './shared/widths';
import { usePathSegments } from './hooks/usePathSegments';
import { GAME_TYPES } from './modules/game/helpers';
import { setGameType } from './modules/game/slice';
import { useAppDispatch } from './core/redux/hooks/useAppDispatch';
import { useAppSelector } from './core/redux/hooks/useAppSelector';
import { selectGameType } from './modules/game/selectors';
import ErrorBoundary from './components/ErrorBoundary';
import { LandscapeWarning } from './components/LandscapeWarning';
import { usePingSession } from './hooks/usePingSession';

import './App.scss';

const InnerApp: React.FC<PropsWithChildren> = ({ children }) => {
  const gameType = useAppSelector(selectGameType);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const FIGMA_DESKTOP_HEIGHT = 1080;
  const FIGMA_TABLET_HEIGHT = 800;
  const FIGMA_PHONE_HEIGHT = 812;

  const isPhone = useMedia(`(max-width: ${maxPhoneWidth}px)`, false);
  const isTablet = useMedia(`(max-width: ${maxTabletWidth}px)`, false);
  const isLandscapePhone = useMedia(
    `(orientation: landscape) and (max-height:${maxLandscapePhoneHeight}px) and (max-width:${maxLandscapePhoneWidth}px)`,
    false,
  );

  const rootRef = useRef<HTMLDivElement>(null);

  usePingSession();
  useResizeScale(rootRef, isPhone ? FIGMA_PHONE_HEIGHT : isTablet ? FIGMA_TABLET_HEIGHT : FIGMA_DESKTOP_HEIGHT);
  useGameEvents();
  usePreventZoom();

  const segments = usePathSegments();

  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        if (gameType) {
          navigate(`/${gameType}/difficulty`);
        }
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [navigate, gameType]);

  useEffect(() => {
    if (segments.length) {
      const type = GAME_TYPES.find((gameType) => segments.includes(gameType));
      if (type) {
        dispatch(setGameType(type));
      }
    }
  }, [dispatch, segments]);

  return (
    <div className="App" id="app" ref={rootRef}>
      <ErrorBoundary>{children}</ErrorBoundary>
      {isLandscapePhone && gameType === 'metro' && <LandscapeWarning />}
    </div>
  );
};

const App: React.FC<PropsWithChildren> = ({ children }) => (
  <MemoryRouter>
    <InnerApp>{children}</InnerApp>
  </MemoryRouter>
);

export default App;
